import { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { CSS } from "@dnd-kit/utilities";
import { cn } from "@reactive-resume/utils";
import { Tooltip } from "@reactive-resume/ui";
import { useSortable } from "@dnd-kit/sortable";
import { DialogName } from "@/client/stores/dialog";
import { SectionKey } from "@reactive-resume/schema";
import AiAssistantIcon from "@/client/pages/builder/Icons/AiAssistantIcon";
import { CaretDown, DotsSixVertical, EyeSlash } from "@phosphor-icons/react";

import { useResumeStore } from "@/client/stores/resume";
import { SectionItemForm } from "../../dialogs/SelectedItemForm";

import { SectionListItemOptions } from "./section-list-item-options";

export type SectionListItemProps = {
  id: string;
  itemIndex: number;
  title: string;
  sectionId: string;
  visible?: boolean;
  canAICustomize?: boolean;
  description?: string;
  isExpanded?: boolean;
  onToggleExpand?: (itemId: string) => void;
  onItemRemoved?: (itemId: string) => void;

  // Callbacks
  onUpdate?: () => void;
  onDuplicate?: () => void;
};

export const SectionListItem = ({
  id,
  itemIndex,
  title,
  sectionId,
  description,
  visible = true,
  canAICustomize = false,
  isExpanded = false,
  onToggleExpand,
  onItemRemoved,
  onUpdate,
  onDuplicate,
}: SectionListItemProps) => {
  const setValue = useResumeStore((state) => state.setValue);

  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 100 : undefined,
    transition,
  };

  const canShowTailor = useMemo(() => {
    return (
      sectionId === "basics" ||
      sectionId === "summary" ||
      sectionId === "experience" ||
      sectionId === "skills" ||
      sectionId.startsWith("custom")
    );
  }, [sectionId]);

  const onToggleCustomization = (e: React.MouseEvent) => {
    e.stopPropagation();
    setValue(`sections.${sectionId}.items[${itemIndex}].canAICustomize`, !canAICustomize);
  };

  return (
    <motion.section
      ref={setNodeRef}
      initial={{
        opacity: 0,
        y: -20,
        scale: 0.95,
      }}
      animate={{
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
          duration: 0.25,
          ease: [0.04, 0.62, 0.23, 0.98],
          opacity: { duration: 0.2 },
          y: { duration: 0.25 },
          scale: { duration: 0.2, delay: 0.05 },
        },
      }}
      exit={{
        opacity: 0,
        x: -30,
        scale: 0.9,
        transition: {
          duration: 0.18,
          ease: [0.4, 0, 0.2, 1],
          opacity: { duration: 0.1 },
          x: { duration: 0.18 },
          scale: { duration: 0.15 },
        },
      }}
    >
      <div style={style} className="group m-2 rounded-xl border bg-secondary/20">
        <div className="flex transition-opacity">
          {/* Drag Handle */}
          <div
            {...listeners}
            {...attributes}
            className={cn(
              "ml-1 flex w-5 cursor-grab items-center justify-center active:cursor-grabbing",
            )}
          >
            <DotsSixVertical weight="bold" size={12} />
          </div>

          {/* Collapsible List Item */}
          <button
            type="button"
            className="relative flex-1 cursor-pointer p-3 text-left"
            onClick={() => {
              onUpdate && onUpdate();
              onToggleExpand && onToggleExpand(id);
            }}
          >
            {/* Title, Description and action icons */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className={cn("font-medium leading-relaxed", !visible && "opacity-50")}>
                  {title}
                </h4>
                {description && <p className="text-xs leading-relaxed opacity-50">{description}</p>}
              </div>
              <div className="flex flex-shrink-0 items-center gap-2">
                {/* Show EyeSlash icon if not visible */}
                {!visible && (
                  <Tooltip content="This item is not visible">
                    <div onClick={(e) => e.stopPropagation()} className="text-red-500 opacity-70">
                      <EyeSlash size={18} />
                    </div>
                  </Tooltip>
                )}

                {/* Show AI icon when customizable is checked & item is visible */}
                {visible && canShowTailor && (
                  <>
                    {canAICustomize ? (
                      <Tooltip content="Customization for each job application using AI is Enabled (click to disable)">
                        <button
                          type="button"
                          onClick={onToggleCustomization}
                          className="flex cursor-pointer items-center gap-1 text-xs hover:opacity-80"
                        >
                          <AiAssistantIcon size={18} />
                        </button>
                      </Tooltip>
                    ) : (
                      <Tooltip content="Customization for each job application using AI is Disabled (click to enable)">
                        <button
                          type="button"
                          onClick={onToggleCustomization}
                          className="flex cursor-pointer items-center gap-1 text-xs opacity-50 grayscale hover:opacity-100"
                        >
                          <AiAssistantIcon size={18} />
                        </button>
                      </Tooltip>
                    )}
                  </>
                )}

                {/* Section item Actions */}
                <div onClick={(e) => e.stopPropagation()}>
                  <SectionListItemOptions
                    id={sectionId as DialogName}
                    index={itemIndex}
                    itemId={id}
                    onDuplicate={onDuplicate}
                    onItemRemoved={onItemRemoved}
                  />
                </div>

                {/* Up/Down icon */}
                <CaretDown
                  size={16}
                  className={cn(
                    "ease-[cubic-bezier(0.04,0.62,0.23,0.98)] transition-transform duration-200",
                    isExpanded ? "rotate-180" : "rotate-0",
                  )}
                />
              </div>
            </div>
          </button>
        </div>
        <AnimatePresence mode="wait">
          {isExpanded && (
            <motion.div
              initial={{
                height: 0,
                opacity: 0,
                scale: 0.95,
              }}
              animate={{
                height: "auto",
                opacity: 1,
                scale: 1,
                transition: {
                  height: { duration: 0.22, ease: [0.04, 0.62, 0.23, 0.98] },
                  opacity: { duration: 0.18, delay: 0.05 },
                  scale: { duration: 0.15, delay: 0.05 },
                },
              }}
              exit={{
                height: 0,
                opacity: 0,
                scale: 0.95,
                transition: {
                  height: { duration: 0.18, ease: [0.04, 0.62, 0.23, 0.98] },
                  opacity: { duration: 0.12 },
                  scale: { duration: 0.12 },
                },
              }}
              className="flex flex-col gap-2 overflow-auto px-4 py-2 text-sm"
            >
              <SectionItemForm id={sectionId as SectionKey} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.section>
  );
};
